import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const statements = [
  "The messy middle is where the magic lives.",
  "Sobriety is strength, not sacrifice.",
  "Community beats competition. Every time.",
  "Cupcakes fix more than business plans ever will.",
  "You don't need permission to start over.",
];

const stats = [
  { value: "08", label: "Episodes deep" },
  { value: "45'", label: "Avg runtime" },
  { value: "100%", label: "Unfiltered" },
  { value: "0", label: "Gurus allowed" },
];

const paths = [
  {
    label: "New here?",
    title: "Start with the origin story",
    episode: "Ep.01 — The Sniffles",
    youtubeId: "rnaUIW1F7ic",
  },
  {
    label: "Want the raw stuff?",
    title: "Go deep on sobriety",
    episode: "Ep.04 — Alcoholic & Great Chef",
    youtubeId: "9sOZys_UEg8",
  },
  {
    label: "Want to think?",
    title: "Get philosophical",
    episode: "Ep.02 — Frogs Can't Become Enlightened",
    youtubeId: "tOE_FBurlwo",
  },
];

const cell = (i) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5, delay: Math.min(i * 0.07, 0.35) },
});

export const Manifesto = () => {
  return (
    <section className="px-5 sm:px-8 lg:px-12 py-14 sm:py-20 md:py-32" data-testid="manifesto-section">
      <div className="grid grid-cols-2 md:grid-cols-12 border-t border-l border-[#232332]">
        {/* Title cell */}
        <motion.div {...cell(0)} className="col-span-2 md:col-span-5 border-b border-r border-[#232332] p-6 sm:p-8 md:p-12 flex flex-col justify-between gap-8 md:min-h-[240px]">
          <p className="overline-tcc">The Field Manual — What we believe</p>
          <div>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9] text-[#F4F2EE] break-words">
              Rules for the<br />
              <span className="text-stroke-pink">unruly</span><span className="text-[#C8FF3E]">.</span>
            </h2>
          </div>
        </motion.div>

        {/* Manifesto statements */}
        <motion.div {...cell(1)} className="col-span-2 md:col-span-7 border-b border-r border-[#232332] p-6 sm:p-8 md:p-12">
          <ol className="space-y-5">
            {statements.map((s, i) => (
              <li key={i} className="flex items-baseline gap-4 sm:gap-5 group" data-testid={`manifesto-line-${i}`}>
                <span className="font-mono-tcc text-xs text-[#C8FF3E] shrink-0">0{i + 1}</span>
                <p className="font-serif-tcc italic text-lg sm:text-xl md:text-2xl text-[#F4F2EE]/90 leading-snug group-hover:text-[#FF3E8A] transition-colors duration-200">
                  {s}
                </p>
              </li>
            ))}
          </ol>
        </motion.div>

        {/* Stats row */}
        {stats.map((stat, i) => (
          <motion.div
            {...cell(2 + i)}
            key={stat.label}
            className="col-span-1 md:col-span-3 border-b border-r border-[#232332] p-5 sm:p-8 hover:bg-[#10101A] transition-colors duration-200"
            data-testid={`stat-${stat.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            <p className="font-display text-4xl sm:text-5xl md:text-6xl text-[#F4F2EE] mb-2">{stat.value}</p>
            <p className="font-mono-tcc text-[10px] uppercase tracking-[0.25em] text-[#9494A6]">{stat.label}</p>
          </motion.div>
        ))}

        {/* Start-here paths */}
        {paths.map((p, i) => (
          <motion.a
            {...cell(6 + i)}
            key={i}
            href={`https://www.youtube.com/watch?v=${p.youtubeId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="col-span-2 md:col-span-4 border-b border-r border-[#232332] p-6 md:p-10 group hover:bg-[#C8FF3E] transition-colors duration-200"
            data-testid={`start-here-path-${i}`}
          >
            <div className="flex items-start justify-between gap-3 mb-8">
              <p className="font-mono-tcc text-[10px] uppercase tracking-[0.25em] text-[#9494A6] group-hover:text-[#08080D]/60 transition-colors duration-200">
                {p.label}
              </p>
              <ArrowUpRight size={18} className="text-[#FF3E8A] group-hover:text-[#08080D] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-200 shrink-0" />
            </div>
            <h3 className="font-display text-2xl md:text-3xl text-[#F4F2EE] group-hover:text-[#08080D] transition-colors duration-200 leading-none mb-3 break-words">
              {p.title}
            </h3>
            <p className="font-mono-tcc text-xs text-[#FF3E8A] group-hover:text-[#08080D]/70 transition-colors duration-200">{p.episode}</p>
          </motion.a>
        ))}
      </div>
    </section>
  );
};
